import ApiService from "src/util/api.service";
import { getLanguageHeaders } from "./commonLanguageHeader";
import { ServicesUrlEndpoints } from "./servicesEndpointConstant";

export interface DistributorLimitations {
  maxAutomations: number | null;
  maxNotifications: number | null;
  maxUsers: number | null;
  maxRecipients: number | null;
  maxInfoNotificationsPerMonth: number | null;
  maxDddDriverParallelExtractions: number | null;
  maxDddVehicleParallelExtractions: number | null;
}

export type DistributorLimitationsPayload = DistributorLimitations;

export interface DistributorLimitCodeValue {
  code?: string | null;
  value?: number | null;
}

export interface DistributorLimitUpdatePayload {
  code: string;
  value: number;
}

export type DistributorLimitationsResponse =
  | Partial<DistributorLimitations>
  | DistributorLimitCodeValue[]
  | null;

export interface Distributor {
  id: string | number;
  name: string;
}

export const getDistributorList = (language?: string) =>
  ApiService.get<{ response: Distributor[] }>(
    ServicesUrlEndpoints.USER_LEVEL_ACCESS.LOOKUP.DISTRIBUTOR_LIST,
    {
      ...getLanguageHeaders(language),
    },
  );

const distributorLimitCodeToField: Record<string, keyof DistributorLimitations> = {
  AUTOMATIONS: "maxAutomations",
  NOTIFICATION_SETTINGS: "maxNotifications",
  USERS: "maxUsers",
  RECIPIENTS: "maxRecipients",
  INFO_NOTIFICATIONS_PER_MONTH: "maxInfoNotificationsPerMonth",
  DDD_DRIVER_PARALLEL_EXTRACTIONS: "maxDddDriverParallelExtractions",
  DDD_VEHICLE_PARALLEL_EXTRACTIONS: "maxDddVehicleParallelExtractions",
};

export const getDistributorLimitFieldByCode = (code?: string | null) =>
  distributorLimitCodeToField[String(code ?? "")];

export const normalizeDistributorLimitationsResponse = (
  value: DistributorLimitationsResponse | unknown
): Partial<DistributorLimitations> | null => {
  if (!value) return null;

  if (Array.isArray(value)) {
    return value.reduce((acc, item) => {
      if (!item || typeof item !== "object") return acc;

      const code = String((item as DistributorLimitCodeValue).code ?? "");
      const field = distributorLimitCodeToField[code];

      if (!field) return acc;

      const limitValue = Number((item as DistributorLimitCodeValue).value);
      acc[field] = Number.isFinite(limitValue) ? limitValue : null;
      return acc;
    }, {} as Partial<DistributorLimitations>);
  }

  return value as Partial<DistributorLimitations>;
};

const getLimitationsEndpoint = (distributorId: string | number) =>
  ServicesUrlEndpoints.DISTRIBUTOR.LIMITATIONS.replace("{distributorId}", String(distributorId));

export const getDistributorLimitations = (distributorId: string | number, language?: string) =>
  ApiService.get<{ response: DistributorLimitationsResponse }>(
    getLimitationsEndpoint(distributorId),
    {
      ...getLanguageHeaders(language),
    }
  );

export const saveDistributorLimitations = (
  distributorId: string | number,
  payload: DistributorLimitUpdatePayload[],
  language?: string
) =>
  ApiService.patch<{ response: DistributorLimitationsResponse }>(
    getLimitationsEndpoint(distributorId),
    payload,
    {
      ...getLanguageHeaders(language),
    }
  );
